<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaButton from './GaiaButton.vue';
import GaiaIconButton from './GaiaIconButton.vue';
import { gaiaDashedButtonOverrides } from '../themes/tokens';
import { gaiaGlyphPaths, gaiaGlyphSize } from '../glyphs';

const props = defineProps({
  /**
   * The noun for what is being added ("filter", "signers", "option"). Composes
   * into "Add filter", which becomes the visible label and, for the icon-only
   * variant, the title and accessible name in one go. A caller whose label
   * isn't verb + noun ("Add to request", "Add selected (3)") passes the slot.
   */
  subject: {
    type: String,
    default: '',
  },
  /**
   * `dashed` is an empty slot inviting a first item — filter bars, condition
   * groups, the analysis picker. `primary` is for an add that is the point of
   * the panel or the commit of a dialog. `default` is every other in-panel add.
   * `icon` is the dashed slot with no room for a label.
   */
  variant: {
    type: String as PropType<'default' | 'dashed' | 'primary' | 'icon'>,
    default: 'default',
  },
  /**
   * `xs` for a repeatable form row (matching `GaiaDeleteButton`'s in-row step),
   * `sm` for a panel or toolbar, `md` for a dialog's commit — where it has to
   * stand level with the Cancel beside it.
   */
  size: {
    type: String as PropType<'xs' | 'sm' | 'md'>,
    default: 'sm',
  },
  /**
   * Off for a dialog's commit button, where the add is the only thing to press
   * and the glyph reads as decoration rather than affordance.
   */
  icon: {
    type: Boolean,
    default: true,
  },
  /** Replaces the composed title — for spelling out why the action is off. */
  title: {
    type: String,
    default: '',
  },
});

const DIMENSION = { xs: 24, sm: 28, md: 30 } as const;

const NAIVE_SIZE = { xs: 'tiny', sm: 'small', md: 'medium' } as const;

const text = computed(() => (props.subject ? `Add ${props.subject}` : 'Add'));

const glyphSize = computed(() => gaiaGlyphSize[props.size]);

// The dash is the whole point of the `dashed` and `icon` variants; the other
// two take the themed default.
const themeOverrides = computed(() =>
  props.variant === 'dashed' || props.variant === 'icon' ? gaiaDashedButtonOverrides : undefined
);
</script>

<template>
  <!-- The plus for putting something into what you are looking at: files onto
       a review, columns onto a view, an action onto an option, a condition onto a
       filter group. Plenty of these do write a new row — "add" describes what
       the user is doing, not what the backend stores, so an add that creates is
       normal and expected.

       Add vs Create is a judgement call for the screen, not a rule to derive.
       "Create playbook" reads right on a page header; "Add action" reads right
       inside the option it belongs to; both may POST. Pick whichever fits the
       surface and stay consistent within it.

       What this component is actually for: the glyph, the size scale and the
       verb + noun label composition live here, so a call site never redraws a
       plus of its own. Before it existed the app had ten hand-rolled plus SVGs
       at three sizes, two viewBoxes and four stroke widths, plus three icon
       packs. That is the drift being harmonised — not the vocabulary. -->
  <gaia-icon-button
    v-if="props.variant === 'icon'"
    :dimension="DIMENSION[props.size]"
    size="tiny"
    :theme-overrides="themeOverrides"
    :title="props.title || text"
    :aria-label="text"
  >
    <svg
      :width="glyphSize"
      :height="glyphSize"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path :d="gaiaGlyphPaths.plus" />
    </svg>
  </gaia-icon-button>
  <gaia-button
    v-else
    :type="props.variant === 'primary' ? 'primary' : undefined"
    :size="NAIVE_SIZE[props.size]"
    :theme-overrides="themeOverrides"
    :title="props.title || undefined"
  >
    <template v-if="props.icon" #icon>
      <svg
        :width="glyphSize"
        :height="glyphSize"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path :d="gaiaGlyphPaths.plus" />
      </svg>
    </template>
    <slot>{{ text }}</slot>
  </gaia-button>
</template>
